/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
/**
 * Hierarchy is like: MasterView -> TemplateView -> PageView
 */
/* eslint-disable import/no-duplicates */
import * as React from 'react';
import { render } from 'react-dom';
import ITemplateModel from './Model';
import PageView from './PageView';
import Expandable from 'apmui/components/Expandable';

type PageModel = Readonly<ITemplateModel>;

function formatError(err: Error) {
    if (err.stack) {
        /* Remove hostname from stack trace */
        return err.stack.replace(/https?:\/\/[^\/]+/g, '');
    }
    return err.toString();
}

function ErrorPage(props: PageModel & { error: Error }) {
    /* eslint-disable react/prop-types */
    /* FIXME: customize error messages */
    return (
        <div className="apmui-content">
            <h2>Error</h2>
            <p>Page with type {props.pageType} failed to render</p>
            {props.error && (
                <Expandable caption="Show additional information" captionClassName="apmui-link">
                    <div className="error-detail">{formatError(props.error)}</div>
                </Expandable>
            )}
        </div>
    );
    /* eslint-enable react/prop-types */
}

function PlaceholderPage(props: PageModel) {
    return <div className="apmui-content">...</div>;
}

interface IMainViewProps {
    model: PageModel;
}

interface IMainViewState {
    error?: Error;
}

class MainView extends React.Component<IMainViewProps, IMainViewState> {
    private page: React.ElementType<PageModel> = React.lazy(() =>
        import(`apmui/page/${this.props.model.pageType}/View`).then((module) =>
            module.default ? module : { default: module }
        )
    );

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    render() {
        const ResolvedPage = this.page;
        return (
            <PageView {...this.props.model}>
                <React.Suspense fallback={<PlaceholderPage {...this.props.model} />}>
                    {this.state.error ? (
                        <ErrorPage error={this.state.error} {...this.props.model} />
                    ) : (
                        <ResolvedPage {...this.props.model} />
                    )}
                </React.Suspense>
            </PageView>
        );
    }
}

/**
 * General UI implementation
 */
class View {
    private root: Element;
    constructor(root: Element) {
        this.root = root;
    }

    /**
     * Render/update UI from provided description
     * @param model  UI Page description object
     */
    render(model: PageModel) {
        render(<MainView model={model} />, this.root);
    }
}

export default View;
